import React, { useState, useRef } from 'react';
import { Zap, Brain, Target, RefreshCw, ChevronRight, Search, X } from 'lucide-react';
import useArrowNav from '../../hooks/useArrowNav';
import {
  safeAsync, Badge, emptyMsg, cardWrap, btnPrimary, btnSm,
  neuShadow, neuShadowSm, neuShadowInset, inp, GRADE_COLORS
} from './crmShared';
import { matchesNameQuery } from '../../lib/nameSearch';

export default function LeadScoreTab({
  leadScores, leadWeights, trainingData, contacts,
  canCreate, canUpdate, seeding, setSeeding, loadExtended
}) {
  const gradesRef = useRef(null);
  const { onKeyDown: handleGradesKeyDown } = useArrowNav(gradesRef, '[data-grade-card]', { horizontal: true, vertical: true, activateOnFocus: true });

  const [expandedGroups, setExpandedGroups] = useState(new Set(['A+', 'A', 'B']));
  const [searchQuery, setSearchQuery] = useState('');
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [trainingOpen, setTrainingOpen] = useState(false);

  const toggleGroup = (grade) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(grade)) newSet.delete(grade);
    else newSet.add(grade);
    setExpandedGroups(newSet);
  };

  const toggleAllLeaderboard = () => {
    const allGrades = ['A+', 'A', 'B', 'C', 'D', 'F'];
    if (expandedGroups.size === allGrades.length) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(allGrades));
    }
  };

  const filteredScores = leadScores.filter(s =>
    matchesNameQuery([s.first_name, s.last_name, s.pipeline_stage], searchQuery)
  );

  const gradeGroups = { 'A+': [], 'A': [], 'B': [], 'C': [], 'D': [], 'F': [] };
  filteredScores.forEach(s => { if (gradeGroups[s.grade]) gradeGroups[s.grade].push(s); });

  // Auto-expand on search
  if (searchQuery && expandedGroups.size === 0) {
    setExpandedGroups(new Set(['A+', 'A', 'B', 'C', 'D', 'F']));
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Brain size={18} color="#256F7A" /> Predictive Lead Scoring
        </h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {canCreate && leadWeights.length === 0 && (
            <button onClick={safeAsync(async () => { setSeeding(true); await window.zignagenixAPI.seedLeadScoreWeights(); await loadExtended(); setSeeding(false); })}
              disabled={seeding} style={{ ...btnPrimary, background: 'linear-gradient(145deg, #a78bfa, #2E8B98)' }}>
              <Zap size={13} /> Seed Score Weights
            </button>
          )}
          {canUpdate && contacts.length > 0 && (
            <button onClick={safeAsync(async () => {
              setSeeding(true);
              let failedCount = 0;
              for (const c of contacts) {
                try {
                  await window.zignagenixAPI.scoreContact(c.id);
                } catch (err) {
                  console.error("[LeadScore] Scoring failed for contact:", c?.id, err.message);
                  failedCount++;
                }
              }
              if (failedCount > 0) {
                alert(`Scoring completed. ${failedCount} contact(s) failed. Check console for details.`);
              }
              await loadExtended(); setSeeding(false);
            })} disabled={seeding} style={btnPrimary}>
              <Brain size={13} /> {seeding ? 'Scoring...' : 'Score All Contacts'}
            </button>
          )}
        </div>
      </div>

      {/* Score Summary */}
      {leadScores.length > 0 && (() => {
        const gradeGroups = { 'A+': [], 'A': [], 'B': [], 'C': [], 'D': [], 'F': [] };
        leadScores.forEach(s => { if (gradeGroups[s.grade]) gradeGroups[s.grade].push(s); });
        return (
          <div ref={gradesRef} onKeyDown={handleGradesKeyDown} style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            {Object.entries(gradeGroups).map(([g, items]) => (
              <div key={g} data-grade-card tabIndex={0} style={{ background: '#F8F9FA', borderRadius: 12, boxShadow: neuShadow, padding: '12px 20px', textAlign: 'center', minWidth: 80, outline: 'none', cursor: 'pointer' }}
                onFocus={(e) => { e.currentTarget.style.boxShadow = `${neuShadow}, 0 0 0 2px ${GRADE_COLORS[g]}`; }}
                onBlur={(e) => { e.currentTarget.style.boxShadow = neuShadow; }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: GRADE_COLORS[g] }}>{items.length}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: GRADE_COLORS[g] }}>Grade {g}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Scored Contacts Leaderboard */}
      {leadScores.length === 0 ? (
        <div style={{ ...cardWrap, marginBottom: 20, padding: 20 }}>
          <div style={emptyMsg}>No contacts scored yet. Seed weights and click "Score All Contacts".</div>
        </div>
      ) : (
        <div style={{ ...cardWrap, marginBottom: 20 }}>
          {/* Search and controls */}
          <div style={{ padding: 16, borderBottom: '1px solid #e0e5ec', display: 'flex', gap: 8, alignItems: 'center' }}>
            <Search size={16} color="#6C757D" />
            <input type="text" placeholder="Search by name or stage..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              style={{ ...inp, flex: 1, margin: 0 }} />
            {searchQuery && <button onClick={() => setSearchQuery('')} style={{ ...btnSm, padding: '4px 8px' }}><X size={14} /></button>}
            <button onClick={toggleAllLeaderboard} style={{ ...btnSm }}>
              {expandedGroups.size > 0 ? 'Collapse All' : 'Expand All'}
            </button>
          </div>

          {/* Grade groups */}
          {Object.entries(gradeGroups).map(([grade, items]) => {
            const gc = GRADE_COLORS[grade] || '#6C757D';
            const isOpen = expandedGroups.has(grade);
            return (
              <div key={grade}>
                <button
                  onClick={() => toggleGroup(grade)}
                  style={{
                    width: '100%', padding: '12px 16px', border: 'none', background: '#F8F9FA',
                    display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    fontSize: 13, fontWeight: 600, color: '#212529', borderBottom: '1px solid #e0e5ec',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f2f7'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#F8F9FA'}
                >
                  <ChevronRight
                    size={16}
                    color={gc}
                    style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease', flexShrink: 0 }}
                  />
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: `${gc}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: gc }}>Grade {grade}</span>
                  </div>
                  <Badge text={items.length} color={gc} />
                  <span style={{ fontSize: 11, color: '#ADB5BD', marginLeft: 'auto' }}>
                    {items.length} contact{items.length !== 1 ? 's' : ''}
                  </span>
                </button>

                {/* Contacts in group */}
                {isOpen && (
                  <div style={{
                    maxHeight: '1000px',
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease',
                  }}>
                    {items.length === 0 ? (
                      <div style={{ padding: '12px 16px', color: '#ADB5BD', fontSize: 12 }}>No contacts with this grade</div>
                    ) : (
                      items.map((s, i) => (
                        <div key={s.id} style={{ padding: '12px 16px', borderBottom: '1px solid #e0e5ec', display: 'flex', alignItems: 'center', gap: 14, background: i % 2 === 1 ? '#fff' : '#F8F9FA' }}>
                          <div style={{ width: 28, fontSize: 11, fontWeight: 700, color: '#ADB5BD', textAlign: 'center' }}>#{leadScores.indexOf(s) + 1}</div>
                          <div style={{ width: 40, height: 40, borderRadius: 12, background: `${gc}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ fontSize: 16, fontWeight: 800, color: gc }}>{s.grade}</span>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#212529' }}>{s.first_name} {s.last_name}</div>
                            <div style={{ fontSize: 11, color: '#6C757D' }}>{s.pipeline_stage || 'No stage'}</div>
                          </div>
                          {/* Score bar */}
                          <div style={{ width: 120 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                              <span style={{ fontSize: 10, color: '#6C757D' }}>Score</span>
                              <span style={{ fontSize: 11, fontWeight: 700, color: gc }}>{s.total_score}</span>
                            </div>
                            <div style={{ background: '#e0e5ec', borderRadius: 4, height: 6, boxShadow: neuShadowInset }}>
                              <div style={{ width: `${Math.min(100, s.prediction_pct || 0)}%`, height: '100%', background: gc, borderRadius: 4 }} />
                            </div>
                          </div>
                          <div style={{ textAlign: 'center', minWidth: 60 }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: gc }}>{s.prediction_pct || 0}%</div>
                            <div style={{ fontSize: 9, color: '#ADB5BD' }}>Likelihood</div>
                          </div>
                          {/* Score single contact */}
                          <button onClick={safeAsync(async () => { await window.zignagenixAPI.scoreContact(s.contact_id); loadExtended(); })}
                            style={{ ...btnSm, padding: '4px 8px' }} title="Re-score"><RefreshCw size={12} /></button>
                          {/* Record outcome */}
                          <select defaultValue="" onChange={async (e) => { if (e.target.value) { await window.zignagenixAPI.recordLeadOutcome(s.contact_id, e.target.value); loadExtended(); e.target.value = ''; } }}
                            style={{ fontSize: 10, padding: '3px 6px', borderRadius: 6, border: 'none', background: '#F8F9FA', boxShadow: neuShadowSm, cursor: 'pointer' }}>
                            <option value="">Outcome</option>
                            <option value="converted">Converted</option>
                            <option value="lost">Lost</option>
                          </select>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Scoring Weights */}
      {leadWeights.length > 0 && (
        <div style={{ ...cardWrap, marginBottom: 20 }}>
          <button
            onClick={() => setWeightsOpen(!weightsOpen)}
            style={{
              width: '100%', padding: '16px', border: 'none', background: '#F8F9FA',
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              fontSize: 13, fontWeight: 700, color: '#212529',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f0f2f7'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#F8F9FA'}
          >
            <ChevronRight
              size={18}
              color="#256F7A"
              style={{ transform: weightsOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
            />
            <Target size={16} color="#256F7A" />
            <span>Scoring Factors & Weights</span>
            <Badge text={leadWeights.length} color="#256F7A" />
          </button>

          {weightsOpen && (
            <div style={{
              maxHeight: '1000px',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease',
              padding: 20,
              borderTop: '1px solid #e0e5ec'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                {leadWeights.map(w => (
                  <div key={w.id} style={{ background: '#F8F9FA', borderRadius: 10, boxShadow: neuShadowSm, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{w.factor_label}</span>
                      <Badge text={`${w.weight}x`} color="#256F7A" />
                    </div>
                    <div style={{ fontSize: 10, color: '#6C757D', marginBottom: 4 }}>{w.description}</div>
                    <Badge text={w.category} color="#2E8B98" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Training Data */}
      {trainingData.length > 0 && (
        <div style={{ ...cardWrap }}>
          <button
            onClick={() => setTrainingOpen(!trainingOpen)}
            style={{
              width: '100%', padding: '16px', border: 'none', background: '#F8F9FA',
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              fontSize: 13, fontWeight: 700, color: '#212529',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f0f2f7'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#F8F9FA'}
          >
            <ChevronRight
              size={18}
              color="#2E8B98"
              style={{ transform: trainingOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
            />
            <Brain size={16} color="#2E8B98" />
            <span>Training Data</span>
            <Badge text={trainingData.length} color="#2E8B98" />
          </button>

          {trainingOpen && (
            <div style={{
              maxHeight: '1000px',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease',
              padding: 20,
              borderTop: '1px solid #e0e5ec'
            }}>
              <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                <div style={{ background: '#e8f5e9', borderRadius: 10, padding: '8px 16px', boxShadow: neuShadowInset }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#4FA038' }}>{trainingData.filter(t => t.outcome === 'converted').length}</span>
                  <span style={{ fontSize: 11, color: '#6C757D', marginLeft: 6 }}>Converted</span>
                </div>
                <div style={{ background: '#fce8e8', borderRadius: 10, padding: '8px 16px', boxShadow: neuShadowInset }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#DC3545' }}>{trainingData.filter(t => t.outcome === 'lost').length}</span>
                  <span style={{ fontSize: 11, color: '#6C757D', marginLeft: 6 }}>Lost</span>
                </div>
              </div>
              <p style={{ fontSize: 11, color: '#6C757D' }}>
                {trainingData.length >= 5 ? 'Predictive model is active — scoring weights auto-adjust based on recorded outcomes.' :
                  `Need ${5 - trainingData.length} more outcomes to activate predictive model.`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
